/* import __COLOCATED_TEMPLATE__ from './conversation-insights.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ConversationalInsightsApi from 'embercom/services/inbox2-conversational-insights-api';
import type SidebarSection from 'embercom/objects/inbox/sidebar-section';
import type RouterService from '@ember/routing/router-service';
import type Session from 'embercom/services/session';
import { type SidebarSectionId } from 'embercom/components/inbox2/conversation-details-sidebar/sidebar-item-container';
import { CX_REASON_TO_COLOR } from 'embercom/components/reporting/automation/ai-insights/conversation-stream';

interface CxAttribute {
  label: string;
  color: string;
}

interface Args {
  conversation: Conversation;
  name: string;
  isOpen: boolean;
  onOpenSectionChange: (value: SidebarSectionId) => (value: SidebarSectionId) => unknown;
  sidebarSection: SidebarSection;
}

interface Signature {
  Args: Args;
}

export default class ConversationInsights extends Component<Signature> {
  @service declare inbox2ConversationalInsightsApi: ConversationalInsightsApi;
  @service declare router: RouterService;
  @service declare session: Session;

  private topicsLoader = DeduplicatedAsyncData(
    this,
    () => [this.args.conversation.id],
    (conversationId, { signal }) => {
      return this.inbox2ConversationalInsightsApi.fetchTopicsForConversation(conversationId, {
        signal,
      });
    },
  );

  get topics() {
    return this.topicsLoader.value || [];
  }

  get isLoadingTopics(): boolean {
    return this.topicsLoader.isLoading;
  }

  get cxScore(): string | number | undefined {
    return this.args.conversation.attributesById?.['customer_experience_rating']?.value as
      | string
      | number
      | undefined;
  }

  get cxExplanation(): string | undefined {
    return this.args.conversation.attributesById?.['customer_experience_explanation']?.value as
      | string
      | undefined;
  }

  get cxAttributes(): CxAttribute[] {
    let raw = this.args.conversation.attributesById?.['customer_experience_attributes']?.value as
      | string
      | undefined;
    if (!raw) {
      return [];
    }
    return raw.split('\n').map((attribute) => {
      let colorConfig = CX_REASON_TO_COLOR[attribute as keyof typeof CX_REASON_TO_COLOR];
      let label = attribute
        .split(':')[0]
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        label,
        color: colorConfig?.color || '',
      };
    });
  }

  get hasCxData(): boolean {
    let score = this.args.conversation.attributesById?.['customer_experience_rating']?.value;
    return score !== undefined && score !== null;
  }

  get exploreTopicsUrl(): string {
    return this.router.urlFor(
      'apps.app.automation.fin-ai-agent.analyze.topics-explorer',
      this.session.workspace,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::ConversationInsights': typeof ConversationInsights;
    'inbox2/conversation-details-sidebar/sidebar-sections/conversation-insights': typeof ConversationInsights;
  }
}
